import { useCallback, useMemo } from 'react';
import { useConfigProperty } from '@leagueplatform/config';
import type { EnvironmentKey } from '@web-config-app/core';
import {
  getTokenForAudience,
  getUrlFromPath,
  configFetch,
} from '@web-config-app/api';
import type { ConfigFetchOptions } from '@web-config-app/api';
import { useAuthoringEnvironments } from '../use-authoring-environments/use-authoring-environments';
import { resolveAuthoringEnvironmentInfo } from '../use-authoring-environment-info/use-authoring-environment-info';

export type UseEnvironmentFetchOptions = Omit<
  ConfigFetchOptions,
  'token' | 'appVersion' | 'clientId'
>;

export type EnvironmentFetch = (
  path: string,
  options?: UseEnvironmentFetchOptions,
  fetchFn?: (path: string, options: ConfigFetchOptions) => Promise<Response>,
) => Promise<Response>;

/**
 * Returns a factory for building a fetch function against *any* environment, resolving the
 * environment when the factory is called rather than when a hook is.
 *
 * Binding the environment at hook-call time forces any operation targeting a different one to
 * remount or wait a render for its hooks to rebind. Nothing here needs to be a hook:
 * `useAuthoringEnvironments()` takes no arguments, so the subscription is identical whichever
 * environment you ask about -- only the Map lookup differs.
 *
 * Prefer {@link useEnvironmentFetch} when the environment genuinely is fixed for the lifetime
 * of the component; use this when the caller chooses the target per operation.
 */
export const useEnvironmentFetchFactory = () => {
  const {
    environmentMap,
    baseAuthoringEnvironment,
    multiEnvironmentAuthoringEnabled,
  } = useAuthoringEnvironments();
  const { clientId, appVersion } = useConfigProperty('core');

  return useCallback(
    (
      environment?: EnvironmentKey,
      entityBaseUrl?: string,
    ): EnvironmentFetch => {
      // Shared with useAuthoringEnvironmentInfo so "which environment does this key mean",
      // including the unknown-key error, has one owner.
      const { apiUrl, audience } = resolveAuthoringEnvironmentInfo(
        environment,
        {
          environmentMap,
          baseAuthoringEnvironment,
          multiEnvironmentAuthoringEnabled,
        },
      );

      return async (path, options, fetchFn = configFetch) => {
        const token = await getTokenForAudience(audience);
        const url = getUrlFromPath(path, entityBaseUrl ?? apiUrl);
        return fetchFn(url, {
          ...options,
          appVersion,
          clientId,
          token,
        });
      };
    },
    [
      environmentMap,
      baseAuthoringEnvironment,
      multiEnvironmentAuthoringEnabled,
      clientId,
      appVersion,
    ],
  );
};

/**
 * Returns a function for making requests on a specific environment.
 *
 * @param environment the environment you wish to make fetch requests against
 */
export const useEnvironmentFetch = (
  environment: EnvironmentKey,
  entityBaseUrl?: string,
) => {
  const createEnvironmentFetch = useEnvironmentFetchFactory();
  return useMemo(
    () => createEnvironmentFetch(environment, entityBaseUrl),
    [createEnvironmentFetch, environment, entityBaseUrl],
  );
};
